import React, { useEffect, useState } from 'react';
import { Flex, useDisclosure } from '@withjoy/joykit';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useTranslation } from '@shared/core';

import { FilterPill } from './components/FilterPill';
import { PillTrigger } from './components/FilterShared';
import type { FilterFacet } from './Filter.types';
import { FiltersDrawer } from './FiltersDrawer';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { MobileFiltersTrigger } from './components/MobileFiltersTrigger';
import { useFilterChangeHandler } from './useFilterChangeHandler';
import { useResponsiveFilterOverflow } from './useResponsiveFilterOverflow';

type FiltersProps = Readonly<{
  facets: FilterFacet[];
  pillHeight?: number;
  /** Whether the overflow ("More") row is currently expanded. Owned by the parent so the
   *  full-width overflow row can render as a sibling of the primary filter row. */
  overflowExpanded: boolean;
  onToggleOverflow: () => void;
}>;

/**
 * The primary (first-row) filter controls: the inline facet pills plus the "More" toggle on
 * desktop, or a single drawer trigger on mobile/tablet. The hidden facets themselves are rendered
 * separately by `FilterOverflowRow` so they can span the full width.
 */
export const Filters = React.memo((props: FiltersProps) => {
  const { tq } = useTranslation('stationery');
  const { facets, pillHeight, overflowExpanded, onToggleOverflow } = props;

  const [defaultActiveDrawerIndex, setDefaultActiveDrawerIndex] = useState(0);

  const isMobileOrTablet = useIsMobileScreen();
  const { isOpen: isDrawerOpen, onOpen: openDrawer, onClose: closeDrawer } = useDisclosure();

  const { filteredFacets, visibleFacets, overflowFacets, hiddenActiveCount } = useResponsiveFilterOverflow(facets);

  useEffect(() => {
    if (!isMobileOrTablet) {
      closeDrawer();
    }
  }, [isMobileOrTablet, closeDrawer]);

  const handleOnFilterChange = useFilterChangeHandler();

  const handleOnTriggerFilterDrawer = useEventCallback((groupCode?: string) => {
    if (groupCode) {
      const targetFacetIndex = filteredFacets.findIndex(facet => facet.groupCode === groupCode);
      setDefaultActiveDrawerIndex(targetFacetIndex !== -1 ? targetFacetIndex : 0);
    } else {
      setDefaultActiveDrawerIndex(-1);
    }

    openDrawer();
  });

  if (filteredFacets.length === 0) {
    return null;
  }

  return (
    <Flex
      flexWrap="wrap"
      alignItems="center"
      gap={4}
    >
      {isMobileOrTablet ? (
        <MobileFiltersTrigger
          label={tq`Filters`}
          onClick={() => {
            handleOnTriggerFilterDrawer();
          }}
        />
      ) : (
        <>
          {visibleFacets.map(facet => (
            <FilterPill
              key={facet.groupCode}
              interaction="popover"
              facet={facet}
              onFilterChange={handleOnFilterChange}
              onTriggerFilterDrawer={handleOnTriggerFilterDrawer}
              pillHeight={pillHeight}
            />
          ))}
          {overflowFacets.length > 0 && (
            <PillTrigger
              label={tq`More`}
              isOpen={overflowExpanded}
              activeFiltersCount={hiddenActiveCount}
              onClick={onToggleOverflow}
              pillHeight={pillHeight}
            />
          )}
        </>
      )}

      <FiltersDrawer
        isOpen={isDrawerOpen}
        onChange={handleOnFilterChange}
        defaultIndex={defaultActiveDrawerIndex}
        onClose={closeDrawer}
        filterFacets={filteredFacets}
      />
    </Flex>
  );
});
